/** Generic entity search autocomplete — works with any Accelo/Zendesk entity type. */

import { useState, useEffect, useRef } from "react";
import { Autocomplete, TextField, Chip, CircularProgress } from "@mui/material";
import { api } from "@/services/api";

interface Entity {
  id: string;
  name?: string;
  title?: string;
  standing?: string;
  email?: string;
  [key: string]: unknown;
}

interface Props {
  entityType: string;
  value: Entity | Entity[] | null;
  onChange: (value: Entity | Entity[] | null) => void;
  multiple?: boolean;
  label?: string;
  helperText?: string;
  pluginId?: string;
  credential?: string;
}

const STANDING_COLOR: Record<string, "success" | "default" | "error"> = {
  active: "success",
  inactive: "default",
  suspended: "error",
};

function displayLabel(e: Entity): string {
  return e.name || e.title || e.email || `#${e.id}`;
}

export default function EntitySearch({ entityType, value, onChange, multiple = false, label = "Search", helperText, pluginId, credential }: Props) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (inputValue.length < 2) {
      setOptions([]);
      setErrorMessage("");
      return;
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      setErrorMessage("");
      try {
        const data = await api.get<{ results: Entity[] }>(
          `/reporting/entities/search?type=${entityType}&q=${encodeURIComponent(inputValue)}${pluginId ? `&pluginId=${pluginId}` : ""}${credential ? `&credential=${credential}` : ""}`
        );
        setOptions(data.results ?? []);
      } catch (err) {
        setOptions([]);
        const message = err instanceof Error ? err.message : "";
        if (message.toLowerCase().includes("credential")) {
          setErrorMessage("Search unavailable - please ask an Admin to review the plugin credential configuration.");
        } else {
          setErrorMessage("Search temporarily unavailable. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [inputValue, entityType, pluginId]);

  return (
    <Autocomplete
      multiple={multiple}
      options={options}
      getOptionLabel={displayLabel}
      filterOptions={(x) => x}
      value={value ?? (multiple ? [] : null)}
      onChange={(_, v) => onChange(v)}
      onInputChange={(_, v) => setInputValue(v)}
      loading={loading}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {displayLabel(option)}
          {option.standing && (
            <Chip label={option.standing} size="small" color={STANDING_COLOR[option.standing] ?? "default"} sx={{ ml: 1 }} />
          )}
        </li>
      )}
      renderTags={(v, getTagProps) =>
        v.map((o, i) => <Chip {...getTagProps({ index: i })} key={o.id} label={displayLabel(o)} size="small" />)
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={errorMessage || helperText}
          error={!!errorMessage}
          size="small"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
